import { useState, useEffect, useCallback, useRef } from 'react';
import { getSessionToken } from '@/lib/api';

/**
 * Custom hook for WebSocket connection to receive real-time workflow updates.
 * Automatically reconnects on disconnection with exponential backoff.
 */
/** RFC 6455 policy-violation close code — the server rejected this identity. */
const POLICY_VIOLATION_CLOSE = 1008;

export function useWorkflowWebSocket(onUpdate) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionCount, setConnectionCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second
  
  // Use ref for callback to avoid reconnection on callback change
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const connect = useCallback(async () => {
    // The endpoint authenticates at the handshake, and there are two ways to
    // present an identity. A Clerk session has a token, which goes in the
    // subprotocol rather than the query string: a query parameter would be
    // recorded in ingress and proxy logs.
    //
    // A dev-access session has none — `AuthContext` registers a getter that
    // resolves to null on purpose, because that session is an httpOnly cookie
    // and a script-readable token would put an Admin credential back in reach.
    // The browser attaches that cookie to the handshake, and `ws_auth` reads it
    // (refusing on `Origin`, which a page cannot forge and cannot set as a
    // header on a `WebSocket`). So a missing token is not a reason to skip the
    // socket — returning here left the whole cookie path unreachable.
    const token = await getSessionToken();

    // Get backend URL and convert to WebSocket URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    
    if (backendUrl) {
      // Convert HTTPS to WSS, HTTP to WS
      // The /api prefix routes through the ingress to the backend
      wsUrl = backendUrl.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:') + '/api/ws/workflow';
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      wsUrl = `${protocol}//${host}/api/ws/workflow`;
    }

    console.log('Connecting to WebSocket:', wsUrl);

    try {
      // No subprotocol when there is no token: `['bearer']` on its own asks the
      // server to negotiate a protocol it has nothing to match against.
      const ws = token
        ? new WebSocket(wsUrl, ['bearer', token])
        : new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          // Handle different message types
          switch (data.type) {
            case 'connection_ack':
              console.log('WebSocket acknowledged:', data.message);
              setConnectionCount(data.connections || 0);
              break;
            case 'pong':
              // Keepalive response, ignore
              break;
            case 'item_created':
            case 'item_updated':
            case 'item_moved':
            case 'status_changed':
            case 'sync_complete':
              console.log('Workflow update received:', data.type, data.item_id);
              if (onUpdateRef.current) {
                onUpdateRef.current(data);
              }
              break;
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // 1008 is the server refusing this identity — reconnecting cannot fix it,
        // and retrying ten times with backoff would just hammer the endpoint with
        // a token it has already rejected. A genuine drop is 1006/1001.
        if (event.code === POLICY_VIOLATION_CLOSE) {
          console.warn('WebSocket refused by the server; not reconnecting');
          return;
        }

        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            baseReconnectDelay * Math.pow(2, reconnectAttempts.current),
            30000 // Max 30 seconds
          );
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          console.log('Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, []); // Remove onUpdate dependency since we use ref

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    
    // Set up ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000); // Ping every 30 seconds

    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    connectionCount,
    sendMessage,
    reconnect: connect,
    disconnect
  };
}

export default useWorkflowWebSocket;
